import { useState, useEffect } from "react";
import toast, { Toaster } from "react-hot-toast";
import {
  Box,
  Container,
  Heading,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  VStack,
  Spinner,
  Alert,
  AlertIcon,
  HStack,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  FormControl,
  FormLabel,
  Input,
  Button,
  SimpleGrid,
  InputGroup,
  InputLeftElement,
} from "@chakra-ui/react";
import PageLoader from "./components/PageLoader";

import api from "./api";
import {
  CopyIcon,
  SearchIcon,
  ChevronUpIcon,
  ChevronDownIcon,
} from "@chakra-ui/icons";

interface Token {
  name: string;
  symbol: string;
  assetSymbol: string;
  baseSymbol: string;
  decimals: number;
  address: string;
  isShortable: boolean;
  categories: string[];
  imageUrl: string;
  coingeckoUrl: string;
  explorerUrl: string;
  isV1Available: boolean;
  isPermitSupported: boolean;
  contractVersion: string;
  _id: string;
}

const DATE_PRESETS = [7, 14, 30, 90] as const;
type DatePreset = (typeof DATE_PRESETS)[number] | null;

function formatDateForInput(date: Date) {
  return date.toISOString().slice(0, 10);
}

function getDateRangeFromPreset(days: number) {
  const end = new Date();
  const start = new Date();
  start.setDate(end.getDate() - days);
  return {
    startDate: formatDateForInput(start),
    endDate: formatDateForInput(end),
  };
}

function Orders() {
  const [tokens, setTokens] = useState<Token[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [editToken, setEditToken] = useState<any>(null);
  const [editValues, setEditValues] = useState({
    points: "",
    noOfTrades: "",
    volume: 0,
  });

  const [isOpen, setIsOpen] = useState(false);
  const [saving, setSaving] = useState(false);

  const [searchTerm, setSearchTerm] = useState("");
  const [sortConfig, setSortConfig] = useState<{
    key: string | null;
    direction: "asc" | "desc";
  }>({ key: null, direction: "asc" });
  const [dateRange, setDateRange] = useState({
    ...getDateRangeFromPreset(90),
  });
  const [selectedPreset, setSelectedPreset] = useState<DatePreset>(90);

  const [currentPage, setCurrentPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const pageSize = 20;

  const [allOrders, setAllOrders] = useState<any[]>([]);
  const [totalLoading, setTotalLoading] = useState(false);

  const fetchTokens = async (page = 1, append = false) => {
    if (!append) {
      setLoading(true);
    } else {
      setLoadingMore(true);
    }
    setError("");
    try {
      const res = await api.get(
        `/api/admin/orders?page=${page}&limit=${pageSize}`
      );
      const newOrders = res?.data?.data?.orders || [];

      if (append) {
        setTokens((prev) => [...prev, ...newOrders]);
      } else {
        setTokens(newOrders);
      }

      setHasMore(newOrders.length !== res?.data?.data?.pagination?.total);
      setEditToken(null);
    } catch (err: any) {
      setError(err?.response?.data?.message || "Error loading tokens");
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  };

  const fetchAllOrders = async () => {
    setTotalLoading(true);
    try {
      const res = await api.get(
        `/api/admin/orders?page=1&limit=10000000&pagination=false`
      );
      const orders =
        res?.data?.data?.orders || res?.data?.orders || res?.data?.data || [];
      setAllOrders(Array.isArray(orders) ? orders : []);
    } catch (err) {
      setAllOrders([]);
    } finally {
      setTotalLoading(false);
    }
  };

  useEffect(() => {
    fetchTokens(1, false);
    fetchAllOrders();
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      if (loadingMore || !hasMore) return;

      const scrollTop = document.documentElement.scrollTop;
      const scrollHeight = document.documentElement.scrollHeight;
      const clientHeight = document.documentElement.clientHeight;

      if (scrollTop + clientHeight >= scrollHeight - 200) {
        setCurrentPage((prev) => prev + 1);
      }
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, [loadingMore, hasMore]);

  useEffect(() => {
    if (currentPage > 1) {
      fetchTokens(currentPage, true);
    }
  }, [currentPage]);

  // const handleEdit = (token: any) => {
  //   setEditToken(token);
  //   setEditValues({
  //     points: token.points ?? "",
  //     noOfTrades: token.noOfTrades ?? "",
  //     volume: token.volume / 10 ** 18,
  //   });
  //   setIsOpen(true);
  // };

  const handleEditChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setEditValues({ ...editValues, [e.target.name]: e.target.value });
  };

  const handleEditSave = async () => {
    if (
      editValues.points === "" ||
      editValues.noOfTrades === "" ||
      // editValues.volume === "" ||
      isNaN(Number(editValues.points)) ||
      isNaN(Number(editValues.noOfTrades)) ||
      isNaN(Number(editValues.volume))
    ) {
      toast.error("All fields are required and must be valid numbers.");
      return;
    }

    setSaving(true);
    try {
      await api.put(`/api/admin/leaderboard/${editToken._id}`, {
        points: editValues.points,
        noOfTrades: editValues.noOfTrades,
        volume: editValues.volume * 10 ** 18,
      });
      toast.success("Orders entry updated!");
      setIsOpen(false);
      setCurrentPage(1);
      fetchTokens(1, false);
      fetchAllOrders();
    } catch (err: any) {
      toast.error(err?.response?.data?.message || "Update failed");
    } finally {
      setSaving(false);
    }
  };

  const handleSort = (key: string) => {
    let direction: "asc" | "desc" = "asc";
    if (sortConfig.key === key && sortConfig.direction === "asc") {
      direction = "desc";
    }
    setSortConfig({ key, direction });
  };

  const applyFilters = (orders: any[]) => {
    let filteredOrders = [...orders];

    if (searchTerm) {
      filteredOrders = filteredOrders.filter((order: any) => {
        const searchLower = searchTerm.toLowerCase();
        const addressMatch = order.address?.toLowerCase().includes(searchLower);
        const txHashMatch = order.txHash?.toLowerCase().includes(searchLower);
        const amountMatch = (order.sizeDeltaUsd / 10 ** 30)
          .toFixed(2)
          .includes(searchTerm);
        return addressMatch || txHashMatch || amountMatch;
      });
    }

    if (dateRange.startDate || dateRange.endDate) {
      filteredOrders = filteredOrders.filter((order: any) => {
        if (!order.createdAt) return false;
        const orderDate = new Date(order.createdAt);
        const start = dateRange.startDate
          ? new Date(dateRange.startDate)
          : new Date(0);
        const end = dateRange.endDate
          ? new Date(dateRange.endDate)
          : new Date();
        end.setHours(23, 59, 59, 999);
        return orderDate >= start && orderDate <= end;
      });
    }

    return filteredOrders;
  };

  const filterAndSortOrders = () => {
    let filteredOrders = applyFilters(tokens);

    if (sortConfig.key) {
      filteredOrders.sort((a: any, b: any) => {
        let aValue = a[sortConfig.key!];
        let bValue = b[sortConfig.key!];

        if (sortConfig.key === "amount") {
          aValue = a.sizeDeltaUsd / 10 ** 30;
          bValue = b.sizeDeltaUsd / 10 ** 30;
        }

        if (sortConfig.key === "createdAt") {
          aValue = a.createdAt ? new Date(a.createdAt).getTime() : 0;
          bValue = b.createdAt ? new Date(b.createdAt).getTime() : 0;
        }

        if (aValue < bValue) {
          return sortConfig.direction === "asc" ? -1 : 1;
        }
        if (aValue > bValue) {
          return sortConfig.direction === "asc" ? 1 : -1;
        }
        return 0;
      });
    }

    return filteredOrders;
  };

  const displayOrders = filterAndSortOrders();

  const totalAmount = applyFilters(allOrders).reduce(
    (sum: number, order: any) =>
      sum + (Number(order?.sizeDeltaUsd) || 0) / 10 ** 30,
    0
  );
  const totalCount = applyFilters(allOrders).length;

  return (
    <Container maxW="container.xl" py={8}>
      <Toaster position="top-right" />
      <VStack spacing={8} align="stretch">
        <Heading as="h1" size="xl" textAlign="center">
          Orders
        </Heading>

        <HStack spacing={4} flexWrap="wrap">
          <InputGroup maxW="300px">
            <InputLeftElement pointerEvents="none">
              <SearchIcon color="gray.300" />
            </InputLeftElement>
            <Input
              placeholder="Search address, txHash, amount"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </InputGroup>

          <FormControl maxW="200px">
            <Input
              type="date"
              placeholder="Start Date"
              value={dateRange.startDate}
              onChange={(e) => {
                setDateRange({ ...dateRange, startDate: e.target.value });
                setSelectedPreset(null);
              }}
            />
          </FormControl>

          <FormControl maxW="200px">
            <Input
              type="date"
              placeholder="End Date"
              value={dateRange.endDate}
              onChange={(e) => {
                setDateRange({ ...dateRange, endDate: e.target.value });
                setSelectedPreset(null);
              }}
            />
          </FormControl>

          <HStack spacing={2}>
            {DATE_PRESETS.map((days) => (
              <Button
                key={days}
                size="sm"
                variant={selectedPreset === days ? "solid" : "outline"}
                colorScheme={selectedPreset === days ? "blue" : "gray"}
                onClick={() => {
                  setDateRange(getDateRangeFromPreset(days));
                  setSelectedPreset(days);
                }}
              >
                {days}d
              </Button>
            ))}
          </HStack>

          {(searchTerm || dateRange.startDate || dateRange.endDate) && (
            <Button
              size="sm"
              onClick={() => {
                setSearchTerm("");
                setDateRange({ startDate: "", endDate: "" });
                setSelectedPreset(null);
                setSortConfig({ key: null, direction: "asc" });
                setCurrentPage(1);
                fetchTokens(1, false);
              }}
            >
              Clear Filters
            </Button>
          )}
        </HStack>

        {loading && <PageLoader message="Loading orders..." />}

        <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
          <Box
            p={5}
            bg="white"
            borderRadius="lg"
            borderWidth="1px"
            borderColor="gray.200"
            boxShadow="sm"
            borderLeftWidth="4px"
            borderLeftColor="blue.400"
          >
            <Box
              fontSize="xs"
              color="gray.500"
              textTransform="uppercase"
              letterSpacing="wide"
              fontWeight="semibold"
            >
              Total Orders
            </Box>
            <Box fontSize="2xl" fontWeight="bold" color="gray.800" mt={1}>
              {totalLoading ? (
                <Spinner size="sm" />
              ) : (
                totalCount.toLocaleString()
              )}
            </Box>
          </Box>
          <Box
            p={5}
            bg="white"
            borderRadius="lg"
            borderWidth="1px"
            borderColor="gray.200"
            boxShadow="sm"
            borderLeftWidth="4px"
            borderLeftColor="green.400"
          >
            <Box
              fontSize="xs"
              color="gray.500"
              textTransform="uppercase"
              letterSpacing="wide"
              fontWeight="semibold"
            >
              Total Amount
            </Box>
            <Box fontSize="2xl" fontWeight="bold" color="gray.800" mt={1}>
              {totalLoading ? (
                <Spinner size="sm" />
              ) : (
                `$${totalAmount.toLocaleString(undefined, {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                })}`
              )}
            </Box>
          </Box>
        </SimpleGrid>

        <SimpleGrid columns={{ base: 1, md: 1 }} spacing={8}>
          {error ? (
            <Alert status="error">
              <AlertIcon />
              {error}
            </Alert>
          ) : (
            <Table
              variant="simple"
              sx={{
                "@media (max-width: 768px)": {
                  display: "block",
                  overflowX: "auto",
                  tr: {
                    display: "block",
                    marginBottom: "1rem",
                    borderBottom: "2px solid #ddd",
                  },
                  td: {
                    display: "block",
                    textAlign: "right",
                    paddingLeft: "50%",
                    position: "relative",
                    "&::before": {
                      content: `attr(data-label)`,
                      position: "absolute",
                      left: 0,
                      width: "50%",
                      paddingLeft: "1rem",
                      fontWeight: "bold",
                      textAlign: "left",
                    },
                  },
                  th: {
                    display: "none",
                  },
                },
              }}
            >
              <Thead>
                <Tr>
                  <Th>Index</Th>
                  <Th>Address</Th>
                  <Th>Tx Hash</Th>
                  <Th>Market</Th>
                  <Th cursor="pointer" onClick={() => handleSort("amount")}>
                    <HStack spacing={1}>
                      <span>Amount</span>
                      {sortConfig.key === "amount" &&
                        (sortConfig.direction === "asc" ? (
                          <ChevronUpIcon />
                        ) : (
                          <ChevronDownIcon />
                        ))}
                    </HStack>
                  </Th>
                  {/* <Th>Number of Trades</Th>
                  <Th>Volume</Th> */}
                  <Th cursor="pointer" onClick={() => handleSort("createdAt")}>
                    <HStack spacing={1}>
                      <span>Time</span>
                      {sortConfig.key === "createdAt" &&
                        (sortConfig.direction === "asc" ? (
                          <ChevronUpIcon />
                        ) : (
                          <ChevronDownIcon />
                        ))}
                    </HStack>
                  </Th>
                  {/* <Th>Actions</Th> */}
                </Tr>
              </Thead>
              <Tbody>
                {displayOrders.map((token: any, idx) => (
                  <Tr key={token?.address + idx}>
                    <Td data-label="Index">{idx + 1}</Td>
                    <Td data-label="Address">
                      <Box display="flex" alignItems="center">
                        <a rel="noopener noreferrer" style={{ marginRight: 8 }}>
                          {token?.address?.slice(0, 6)}...
                          {token?.address?.slice(-4)}
                        </a>
                        <CopyIcon
                          cursor="pointer"
                          onClick={() => {
                            navigator.clipboard.writeText(token?.address);
                            toast.success("Address copied!");
                          }}
                        />
                      </Box>
                    </Td>
                    <Td data-label="Tx Hash">
                      {" "}
                      <Box display="flex" alignItems="center">
                        <a rel="noopener noreferrer" style={{ marginRight: 8 }}>
                          {token?.txHash?.slice(0, 6)}...
                          {token?.txHash?.slice(-4)}
                        </a>
                        <CopyIcon
                          cursor="pointer"
                          onClick={() => {
                            navigator.clipboard.writeText(token?.txHash);
                            toast.success("Address copied!");
                          }}
                        />
                      </Box>
                    </Td>
                    <Td>
                      <Box display="flex" alignItems="center">
                        <a rel="noopener noreferrer" style={{ marginRight: 8 }}>
                          {token?.market?.slice(0, 6)}...
                          {token?.market?.slice(-4)}
                        </a>
                        <CopyIcon
                          cursor="pointer"
                          onClick={() => {
                            navigator.clipboard.writeText(token?.market);
                            toast.success("Address copied!");
                          }}
                        />
                      </Box>
                    </Td>
                    <Td>${(token?.sizeDeltaUsd / 10 ** 30)?.toFixed(2)}</Td>
                    {/* <Td>{token?.volume / 10 ** 18}</Td> */}
                    <Td>
                      {token?.updatedAt
                        ? new Date(token?.createdAt).toLocaleString()
                        : ""}
                    </Td>
                    {/* <Td>
                      <HStack spacing={2}>
                        <IconButton
                          aria-label="Edit token"
                          icon={<EditIcon />}
                          size="sm"
                          onClick={() => handleEdit(token)}
                        />
                      </HStack>
                    </Td> */}
                  </Tr>
                ))}
              </Tbody>
            </Table>
          )}

          {loadingMore && (
            <Box textAlign="center" py={4}>
              <Spinner size="md" />
              <Box mt={2}>Loading more orders...</Box>
            </Box>
          )}

          {!hasMore && displayOrders.length > 0 && (
            <Box textAlign="center" py={4} color="gray.500">
              No more orders to load
            </Box>
          )}
        </SimpleGrid>
      </VStack>

      {/* Edit Modal */}
      <Modal isOpen={isOpen} onClose={() => setIsOpen(false)} isCentered>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Edit Orders Entry</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <FormControl mb={3}>
              <FormLabel>Points</FormLabel>
              <Input
                name="points"
                type="number"
                value={editValues?.points}
                onChange={handleEditChange}
              />
            </FormControl>
            <FormControl mb={3}>
              <FormLabel>Number of Trades</FormLabel>
              <Input
                name="noOfTrades"
                type="number"
                value={editValues?.noOfTrades}
                onChange={handleEditChange}
              />
            </FormControl>
            <FormControl mb={3}>
              <FormLabel>Volume</FormLabel>
              <Input
                name="volume"
                type="number"
                value={editValues?.volume}
                onChange={handleEditChange}
              />
            </FormControl>
          </ModalBody>
          <ModalFooter>
            <Button
              colorScheme="blue"
              mr={3}
              onClick={handleEditSave}
              isLoading={saving}
            >
              Save
            </Button>
            <Button onClick={() => setIsOpen(false)}>Cancel</Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Container>
  );
}

export default Orders;